import { useEffect, useCallback, useRef } from "react";
import useGroupLinkStore from "../store/groupLinkStore";

/**
 * Hook for single-symbol panels (emitter + receiver)
 *
 * Single-symbol panels both emit and receive symbol changes:
 * - When their symbol changes locally, they propagate to other receivers in the group
 * - When another panel in the group changes symbol, they receive the update
 *
 * @param {string} panelId - Unique identifier for the panel
 * @param {number|null} group - Current group (null = no group)
 * @param {function} setSymbol - Function to change the panel's symbol
 * @returns {{ emitSymbol: (symbol: string) => void }}
 *
 * @example
 * const ChartPanel = ({ chartId, symbol, group, onSymbolChange }) => {
 *   const { emitSymbol } = useGroupLink(chartId, group, (newSymbol) => {
 *     onSymbolChange(newSymbol);
 *   });
 *
 *   const handleSymbolChange = (newSymbol) => {
 *     onSymbolChange(newSymbol);
 *     emitSymbol(newSymbol); // Propagate to group
 *   };
 * };
 */
export function useGroupLink(panelId, group, setSymbol) {
  const registerReceiver = useGroupLinkStore((s) => s.registerReceiver);
  const unregisterReceiver = useGroupLinkStore((s) => s.unregisterReceiver);
  const propagateSymbol = useGroupLinkStore((s) => s.propagateSymbol);

  // Keep stable reference to setSymbol callback
  const setSymbolRef = useRef(setSymbol);
  setSymbolRef.current = setSymbol;

  // Track previous group to handle group changes
  const prevGroupRef = useRef(group);

  // Register as receiver when group changes
  useEffect(() => {
    // Wrap setSymbol to always use latest reference
    const wrappedSetSymbol = (symbol) => setSymbolRef.current(symbol);

    // If group changed, unregister from old group first
    if (prevGroupRef.current !== group) {
      unregisterReceiver(prevGroupRef.current, panelId);
      prevGroupRef.current = group;
    }

    const cleanup = registerReceiver(group, panelId, wrappedSetSymbol);

    return () => {
      cleanup();
    };
  }, [group, panelId, registerReceiver, unregisterReceiver]);

  // Function to emit symbol change to group
  const emitSymbol = useCallback(
    (symbol) => {
      propagateSymbol(group, symbol, panelId);
    },
    [group, panelId, propagateSymbol],
  );

  return { emitSymbol };
}

/**
 * Hook for multi-symbol panels (emitter only)
 *
 * Multi-symbol panels (like watchlists, scanners) only emit symbol selections.
 * They don't receive symbol changes from other panels.
 *
 * @param {number|null} group - Current group (null = no group)
 * @returns {{ emitSymbol: (symbol: string) => void }}
 *
 * @example
 * const WatchlistPanel = ({ group }) => {
 *   const { emitSymbol } = useGroupEmitter(group);
 *
 *   const handleRowClick = (symbol) => {
 *     emitSymbol(symbol); // Propagate to charts in group
 *   };
 * };
 */
export function useGroupEmitter(group) {
  const propagateSymbol = useGroupLinkStore((s) => s.propagateSymbol);

  const emitSymbol = useCallback(
    (symbol) => {
      propagateSymbol(group, symbol, null);
    },
    [group, propagateSymbol],
  );

  return { emitSymbol };
}
